import { PageTitleNavs } from "@/common/components/page-title-navs";
import Scanner, { ScannedItem } from "@/common/components/scan";
import {
  DELIVERIES,
  // RECALL_PRODUCT,
  RECALL_RESPONSE,
} from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { handleScannedItem, scanTableHeaders, scanTableKeys } from "../data";
import { SellingLevelPriceModal } from "@/pages/dashboard/add-inventory/subs/SellingLevelPriceModal";
import { IUnpricedReceivedProduct } from "@/common/types/pricing";

const ScanWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2rem;

  @media (max-width: ${({ theme }) => theme.screen.mobile}) {
    padding: 2rem 0.5rem;
  }

  .text-wrapper {
    padding: 2rem 2rem 1rem;
    text-align: center;
    border-bottom: 1px solid ${({ theme }) => theme.border.light};

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      color: ${({ theme }) => theme.text.primary};
      text-transform: capitalize;

      @media (max-width: ${({ theme }) => theme.screen.mobile}) {
        font-size: 1.1rem;
      }
    }
  }

  .containerWrapper {
    background-color: white;
    padding: 2rem 3rem;
    width: 100%;
    .top-wrapper {
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }
    .cta {
      margin-top: 1rem;
      display: flex;
      justify-content: center;
    }
  }
`;

function ScanToRecieve() {
  const makeRequest = useApiCallHandling();
  const navigate = useNavigate();
  const [loadingRecall, setLoadingRecall] = useState<boolean>(false);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("from");
  const isReturn = searchParams.get("is_return") === "true";
  const [serialKey, setSerailKey] = useState<string[]>([]);

  // MED-667 (UC-LST-01 Entry B): the products this receipt delivered that are
  // still unpriced, prompted one at a time.
  const [pricingQueue, setPricingQueue] = useState<IUnpricedReceivedProduct[]>([]);
  const [pricingIndex, setPricingIndex] = useState(0);

  const handleScanSerial = (serial: string): Promise<ScannedItem[]> => {
    return new Promise((resolve) => {
      makeRequest(
        RECALL_RESPONSE + `/identify?serial_number=${serial}`,
        "GET",
        {},
        (data) => {
          resolve(handleScannedItem(data));
          setSerailKey((prev) => [...prev, serial]);
        },
        () => resolve([]),
        () => {},
      );
    });
  };

  const handleProceed = (items: ScannedItem[]) => {
    const payload = {
      serial_numbers: items.map((item) => item.unitSerial?.value),
      scanned_serials: serialKey,
    };
    setLoadingRecall(true);
    makeRequest(
      RECALL_RESPONSE + "/batch-recover",
      "POST",
      payload,
      () => navigate("/dashboard/recall"),
      console.log,
      () => setLoadingRecall(false),
    );
  };

  const leaveScanScreen = useCallback(() => {
    navigate(source === "recall" ? "/dashboard/recall" : "/dashboard/deliveries");
  }, [navigate, source]);

  // The receipt has already committed by the time this runs. Every branch here
  // still leaves the screen: pricing is a nudge, never a gate on receiving. An
  // absent or malformed `unpriced_products` therefore behaves exactly as the
  // screen did before this ticket.
  const handleReceived = useCallback(
    (data: any) => {
      const queue: IUnpricedReceivedProduct[] = Array.isArray(data?.unpriced_products)
        ? data.unpriced_products.filter(
            (item: IUnpricedReceivedProduct) => item?.product_id,
          )
        : [];

      if (queue.length === 0) {
        leaveScanScreen();
        return;
      }

      setPricingQueue(queue);
      setPricingIndex(0);
    },
    [leaveScanScreen],
  );

  // Save and "Skip for now" both land here.
  function advancePricing() {
    const next = pricingIndex + 1;
    if (next >= pricingQueue.length) {
      leaveScanScreen();
      return;
    }
    setPricingIndex(next);
  }

  // The X and the backdrop: the seller wants out. Abandon what is left rather
  // than charging a five-product receipt five dismissals. Nothing is stored, and
  // the products stay findable on the inventory screen via Entry A.
  function dismissPricing() {
    setPricingQueue([]);
    leaveScanScreen();
  }

  const handleConfirmDelivery = useCallback(
    (items: ScannedItem[]) => {
      setLoadingRecall(true);
      const payload = {
        serial_numbers: items.map((item) => item.unitSerial?.value),
        scanned_serials: serialKey,
      };
      makeRequest(
        isReturn
          ? `${RECALL_RESPONSE}/batch-recover`
          : DELIVERIES + `/${id}/receive`,
        isReturn ? "POST" : "PATCH",
        payload,
        // A return goes through batch-recover, which is not a delivery receipt
        // and carries no listing semantics — it leaves as it always did.
        isReturn ? leaveScanScreen : handleReceived,
        console.log,
        () => setLoadingRecall(false),
      );
    },
    [id, isReturn, serialKey, makeRequest, leaveScanScreen, handleReceived],
  );

  const link =
    source === "recall" ? "/dashboard/recall" : "/dashboard/deliveries";

  return (
    <ScanWrapper>
      <PageTitleNavs
        title="Scan To Receive"
        backLink={link}
        bcrumbs={[
          {
            title: `${source === "recall" ? "Recall" : "Deliveries"}`,
            link: link,
          },
        ]}
      />
      <div className="text-wrapper">
        <h2>Scan To Receive</h2>
      </div>
      <div className="containerWrapper">
        <Scanner
          tableHeaders={scanTableHeaders}
          tableKeys={scanTableKeys}
          onScanSerial={handleScanSerial}
          onProceed={
            source === "recall" ? handleProceed : handleConfirmDelivery
          }
          loadingProceed={loadingRecall}
          proceedLabel="Proceed"
        />
      </div>

      {/* MED-667. Keyed on the product so switching queue entries mounts a fresh
          modal instance: `listing`/`level`/`price`/`error` are local state that
          would otherwise carry over from the previous product, and a failed
          fetch for the new product would leave the OLD ladder on screen —
          save-able against the NEW productId — without the remount. */}
      {pricingQueue[pricingIndex] && (
        <SellingLevelPriceModal
          key={pricingQueue[pricingIndex].product_id}
          isOpen
          skippable
          close={dismissPricing}
          productId={pricingQueue[pricingIndex].product_id}
          productName={pricingQueue[pricingIndex].name || "This product"}
          queuePosition={{ index: pricingIndex, total: pricingQueue.length }}
          onDone={advancePricing}
        />
      )}
    </ScanWrapper>
  );
}

export default ScanToRecieve;
